"use client";

import { createContext, useContext, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, isFirebaseConfigured, isFirebaseDisabled } from '@/lib/firebase';

const shouldBypassAuth = () => !isFirebaseConfigured || isFirebaseDisabled;
import { useRouter } from "next/navigation";
import { UserData, UserRole } from "@/types/user";
import { UserService } from "@/services/user.service";
import Cookies from "js-cookie";
import { toast } from "react-toastify";


interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  userData: UserData | null;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  signup: (email: string, password: string, role: UserRole, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  isAdmin: boolean;
  refreshToken: () => Promise<string>;
  refreshUserData: () => Promise<void>;
  getApiToken: () => Promise<string>;
  hasPageAccess: (page: string) => boolean;
}

// Create default context value
const defaultContextValue: AuthContextType = {
  user: null,
  userData: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  login: async () => { },
  signup: async () => { },
  logout: async () => { },
  updateUserRole: async () => { },
  isAdmin: false,
  refreshToken: async () => "",
  refreshUserData: async () => { },
  getApiToken: async () => "",
  hasPageAccess: () => false,
};

// Create the context with default value
const AuthContext = createContext<AuthContextType>(defaultContextValue);

// Create the hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === defaultContextValue) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Create the provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const router = useRouter();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Initialize auth state
  useEffect(() => {

    const initializeAuth = async () => {
      // Demo mode: bypass Firebase entirely when not configured
      if (shouldBypassAuth()) {
        // Check if user has completed demo registration
        const demoReg = typeof window !== "undefined" ? localStorage.getItem("demo_registration") : null;
        if (!demoReg) {
          // Not registered — stop loading so the login/registration page renders
          setLoading(false);
          // Redirect to login page unless already on a public page (login, privacy)
          const path = typeof window !== "undefined" ? window.location.pathname : "";
          const isPublicPage = path.startsWith("/login") || path.startsWith("/privacy");
          if (typeof window !== "undefined" && !isPublicPage) {
            router.replace("/login");
          }
          return;
        }
        const regData = JSON.parse(demoReg);
        const demoUserData: UserData = {
          uid: "demo-admin-001",
          email: regData.email || "admin@demo.cbridge.cloud",
          username: regData.name || "Demo Admin",
          role: "admin" as UserRole,
          isActive: true,
          createdAt: new Date("2024-01-01"),
          lastLoginAt: new Date(),
          permissions: { pages: [], areas: [], devices: [], scenes: [] },
          profile_image: "",
        };
        // Set a mock Firebase User shape so layout guards that check `user` pass
        setUser({ uid: "demo-admin-001", email: regData.email || "admin@demo.cbridge.cloud" } as unknown as User);
        setUserData(demoUserData);
        setIsAuthenticated(true);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        // Set persistence before setting up the listener
        await setPersistence(auth, browserLocalPersistence);

        // Only set up listener if not already set up
        // YEH LISTENER FIREBASE USER STATE CHANGE DETECT KARTA HAI
        // Jab bhi user login/logout karta hai, ye automatically trigger hota hai
        // GOOGLE LOGIN KE CASE ME: Jab handleGoogleSignIn me signInWithPopup() call hota hai,
        // Firebase automatically user ko authenticate kar deta hai aur ye listener trigger ho jata hai
        // Isliye handleGoogleSignIn me sirf backend API call karke redirect karna hota hai,
        // User state automatically yaha set ho jata hai
        if (!unsubscribeRef.current) {
          unsubscribeRef.current = onAuthStateChanged(auth, async (user) => {

            if (user) {
              try {
                // Simple flow: user in Firebase = allow. Get from local DB; if not found, ensure-user (add once) then get again.
                let backendUserData = await UserService.getUserFromBackend(user.uid);
                if (!backendUserData) {
                  const token = await user.getIdToken();
                  const ensureRes = await fetch('/api/users/ensure-user', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                  });
                  const ensureData = ensureRes.ok ? await ensureRes.json() : null;
                  if (ensureRes.status === 503) {
                    console.warn('AuthContext: Firebase Admin not configured on backend. Ensure cbridge-1-firebase-adminsdk.json is mounted.');
                  }
                  if (ensureData?.success) {
                    backendUserData = await UserService.getUserFromBackend(user.uid);
                  }
                }

                if (backendUserData) {
                  if (backendUserData.isActive === false) {
                    setUserData(null);
                    setUser(null);
                    setIsAuthenticated(false);
                    firebaseSignOut(auth);
                    setShowInactiveModal(true);
                    setLoading(false);
                    return;
                  }
                  const userData = {
                    ...backendUserData,
                    uid: user.uid,
                    profile_image: backendUserData.profile_image || '',
                    permissions: backendUserData.permissions || { pages: [], areas: [], devices: [], scenes: [] },
                  };
                  setUserData(userData);
                  UserService.updateLastLogin(user.uid).catch(() => {/* Firestore may not be available */});
                }

                const token = await user.getIdToken();
                Cookies.set("token", token, {
                  expires: 7,
                  secure: process.env.NODE_ENV === "production",
                  sameSite: "lax",
                });
              } catch (error) {
                console.error("AuthContext: Error on auth state:", error);
                // Fall back to basic user data from Firebase so the app still loads
                if (!userData) {
                  setUserData({
                    uid: user.uid,
                    email: user.email || "",
                    username: user.displayName || user.email?.split("@")[0] || "User",
                    role: "user" as UserRole,
                    isActive: true,
                    createdAt: new Date(),
                    lastLoginAt: new Date(),
                    permissions: { pages: [], areas: [], devices: [], scenes: [] },
                    profile_image: user.photoURL || "",
                  });
                }
              }
            } else {
              setUserData(null);
              Cookies.remove("token");
            }

            setUser(user);
            setIsAuthenticated(!!user);
            setLoading(false);
            setError(null);
          });
        }

      } catch (error) {
        console.error("AuthContext: Error during initialization:", error);
        setError(
          error instanceof Error
            ? error
            : new Error("Auth initialization failed"),
        );
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [router]);

  const isAdmin = userData?.role === "admin";

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('token', data.token);
        return data.token;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (!user?.uid) return;
    try {
      const res = await fetch('/api/users/get-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firebaseuid: user.uid }),
      });
      const data = await res.json();
      if (data?.success && data?.data) {
        const backend = data.data;
        setUserData((prev) => ({
          ...prev,
          uid: user.uid,
          email: backend.email ?? prev?.email,
          username: backend.username ?? prev?.username,
          role: backend.role ?? prev?.role,
          isActive: backend.isActive !== undefined ? backend.isActive : prev?.isActive,
          permissions: backend.permissions ?? prev?.permissions,
          profile_image: backend.profile_image ?? prev?.profile_image,
        } as UserData));
      }
    } catch (err) {
      console.warn('refreshUserData failed:', err);
    }
  };

  const getApiToken = async (): Promise<string> => {
    if (!user) return "";
    try {
      return await user.getIdToken(false) || "";
    } catch {
      return "";
    }
  };

  const generateToken = async (userData: any) => {
    console.log(userData, "userDatadsdsdsd");

    try {
      if (!userData?.uid) {
        throw new Error('No user logged in');
      }

      const res = await fetch('/api/auth/generate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: userData.uid })
      });
      const data = await res.json();

      if (data.success) {
        localStorage.setItem('refreshToken', data.refreshToken);
        router.push('/dashboard');

      } else {
        throw new Error('Failed to generate token');
      }
    } catch (error) {
      console.error('Error generating token:', error);
      throw error;
    }
  };
  const hasPageAccess = (page: string) => {
    if (isAdmin) return true;

    const raw = userData?.permissions;
    const pages: string[] = Array.isArray(raw?.pages) ? raw.pages : Array.isArray(raw) ? raw : [];

    if (pages.includes(page)) return true;

    // Parent keys: show "Areas" section if user has any areas/lighting page
    if (page === "areas") {
      return pages.some(
        (p) => p === "areas" || p === "areas-overview" || p === "lighting" || p.startsWith("lighting-")
      );
    }
    // Show "Settings" section if user has any cbridge-settings/settings page
    if (page === "settings") {
      return pages.some(
        (p) =>
          p === "settings" ||
          p === "cbridge-settings" ||
          p === "cbridge_settings" ||
          p.startsWith("settings-")
      );
    }

    return false;
  };
  const value = {
    user,
    userData: userData || null,
    loading,
    error,
    isAuthenticated,
    login: async (email: string, password: string, rememberMe: boolean = false) => {
      try {
        setLoading(true);
        setError(null);
        await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

        // 1. Firebase sign in → user must be in Firebase (allow entry)
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;

        // 2. Check local DB; if not found, add once (ensure-user) then get again
        let backendUserData = await UserService.getUserFromBackend(uid);
        if (!backendUserData) {
          const token = await userCredential.user.getIdToken();
          const ensureRes = await fetch('/api/users/ensure-user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          const ensureJson = ensureRes.ok ? await ensureRes.json() : null;
          if (ensureJson?.success) {
            backendUserData = await UserService.getUserFromBackend(uid);
          }
        }

        if (!backendUserData) {
          await firebaseSignOut(auth);
          setShowUnauthorizedModal(true);
          return;
        }
        if (backendUserData.isActive === false) {
          await firebaseSignOut(auth);
          setShowInactiveModal(true);
          setLoading(false);
          return;
        }

        const userData = {
          ...backendUserData,
          uid,
          profile_image: backendUserData.profile_image || '',
          permissions: backendUserData.permissions || { pages: [], areas: [], devices: [], scenes: [] },
        };
        await generateToken(userData);
        await UserService.updateLastLogin(uid);
        setUserData(userData);
        setUser(userCredential.user);
        setIsAuthenticated(true);
        toast.success("Login successful!");
      } catch (error) {
        console.error('Login error:', error);
        setError(error instanceof Error ? error : new Error('Login failed'));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    signup: async (
      email: string,
      password: string,
      role: UserRole,
      username?: string,
    ) => {
      try {
        setLoading(true);
        setError(null);
        const { user } = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        const userData = await UserService.createUser(user.uid, email, role);
        console.log(userData, "userData");
        console.log({ username: username, email: email, firebaseuid: user.uid, password: password }, "{ username: username, email: email, firebaseuid: user.uid, password: password }");

        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: username, email: email, firebaseuid: user.uid, password: password, role: role })
        });

        if (!res.ok) {
          throw new Error(`Backend error: ${res.status} ${res.statusText}`);
        }

        let data;
        try {
          data = await res.json();
        } catch (parseError) {
          throw new Error("Invalid response from server. Please try again.");
        }

        console.log(data, "data");

        console.log(data, "datadatadatadata");

        if (data.success == true) {
          localStorage.removeItem('refreshToken');

          localStorage.setItem('refreshToken', data?.tokens.refreshToken);
          toast.success("Registration successful!");
          router.push("/dashboard");
        } else {
          throw new Error(data.message || "Oops! Username already taken.");
        }


        setUser(user);
        setUserData(userData);

        // // Get the ID token and set it as a cookie
        // const token = await user.getIdToken();
        // Cookies.set("token", token, {
        //   expires: 7,
        //   secure: process.env.NODE_ENV === "production",
        //   sameSite: "lax",
        // });

        router.replace("/dashboard");
      } catch (error) {
        console.error("Error signing up:", error);
        setError(error instanceof Error ? error : new Error("Signup failed"));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    // signup2: async (
    //   email: string,
    //   password: string,
    //   role: UserRole = "user",
    //   username?: string
    // ) => {
    //   try {
    //     setLoading(true);
    //     setError(null);

    //     // 1️⃣ Firebase signup
    //     const { user } = await createUserWithEmailAndPassword(auth, email, password);

    //     // 2️⃣ Backend registration
    //     const res = await fetch('/api/auth/register', {
    //       method: 'POST',
    //       headers: { 'Content-Type': 'application/json' },
    //       body: JSON.stringify({
    //         username,
    //         email,
    //         firebaseuid: user.uid,
    //         password
    //       })
    //     });

    //     const data = await res.json();

    //     if (!res.ok || !data.success) {
    //       // ❗ rollback firebase user
    //       await user.delete();
    //       throw new Error(data.error || "Registration failed");
    //     }

    //     // 3️⃣ Save refresh token
    //     localStorage.setItem('refreshToken', data.tokens.refreshToken);

    //     // 4️⃣ Firebase ID token cookie
    //     const token = await user.getIdToken();
    //     Cookies.set("token", token, {
    //       expires: 7,
    //       secure: process.env.NODE_ENV === "production",
    //       sameSite: "lax",
    //     });

    //     toast.success("Registration successful!");
    //     router.replace("/dashboard");

    //   } catch (error) {
    //     console.error("Signup error:", error);
    //     setError(error instanceof Error ? error : new Error("Signup failed"));
    //     throw error;
    //   } finally {
    //     setLoading(false);
    //   }
    // },
    logout: async () => {
      try {
        setLoading(true);
        setError(null);

        // Demo mode: no real Firebase session — clear the demo registration so
        // initializeAuth doesn't immediately re-authenticate the demo user.
        if (shouldBypassAuth()) {
          if (typeof window !== "undefined") {
            localStorage.removeItem("demo_registration");
            localStorage.removeItem("refreshToken");
          }
          Cookies.remove("token");
          setUser(null);
          setUserData(null);
          setIsAuthenticated(false);
          setLoading(false);
          // Hard navigation to the login page so all in-memory state resets
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          return;
        }

        await firebaseSignOut(auth);
        setUser(null);
        setUserData(null);
        Cookies.remove("token");
        localStorage.removeItem('refreshToken');
        // Toast message is handled by the calling component to avoid duplicates
      } catch (error) {
        console.error("Error logging out:", error);
        setError(error instanceof Error ? error : new Error("Logout failed"));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    updateUserRole: async (userId: string, role: UserRole) => {
      try {
        setLoading(true);
        setError(null);
        await UserService.updateUserRole(userId, role);
        if (userData) {
          setUserData({ ...userData, role });
        }
      } catch (error) {
        console.error("Error updating user role:", error);
        setError(
          error instanceof Error
            ? error
            : new Error("Failed to update user role"),
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    isAdmin,
    refreshToken,
    refreshUserData,
    getApiToken,
    hasPageAccess,

  };

  const UnauthorizedModal = () => {
    if (!showUnauthorizedModal) return null;

    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
              <span className="text-lg">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
          </div>
          <p className="text-gray-700 mb-6">
            You are not authorized to access the C-Bridge on this device.
          </p>
          <button
            onClick={() => setShowUnauthorizedModal(false)}
            className="w-full rounded-lg px-5 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            OK
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const InactiveUserModal = () => {
    if (!showInactiveModal) return null;

    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
              <span className="text-lg">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Account Inactive</h3>
          </div>
          <p className="text-gray-700 mb-6">
            Your account is inactive. Please contact admin.
          </p>
          <button
            onClick={() => setShowInactiveModal(false)}
            className="w-full rounded-lg px-5 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            OK
          </button>
        </div>
      </div>,
      document.body
    );
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
      <UnauthorizedModal />
      <InactiveUserModal />
    </AuthContext.Provider>
  );
}

// Create the initializer component
export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        setInitialized(true);
      } catch (error) {
        console.error("Error initializing auth:", error);
        setInitialized(true); // Still set initialized to true to prevent infinite loading
      }
    };

    initialize();
  }, []);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return <AuthProvider>{children}</AuthProvider>;
}
